import { Component, inject, OnInit, OnDestroy } from '@angular/core'
import { IconSetService } from '@coreui/icons-angular'
import { iconSubset } from './coreUi-files/icons/icon-subset'
import { Title } from '@angular/platform-browser'
import { RouterOutlet, Router } from '@angular/router'
import { NavigationEnd } from '@angular/router'
import { filter, takeUntil, debounceTime } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { TabService } from '../helper/shared/tab.service'


@Component({
	selector: 'app-root',
	template: '<router-outlet/>',
	imports: [RouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {
	private tabService = inject(TabService)
	private router = inject(Router)
	private destroy$ = new Subject<void>()

	title = 'سامانه lmsPlus'
	tabs = this.tabService.tabs

	constructor() {
		const titleService = inject(Title)
		const iconSetService = inject(IconSetService)

		titleService.setTitle(this.title)
		// iconSet singleton
		iconSetService.icons = {...iconSubset}
	}

	ngOnInit(): void {
		// Initialize default tab if needed first
		this.initializeDefaultTab()

		// Set up global route handler with a small delay to ensure router is ready
		setTimeout(() => {
			this.setupRouteHandler()
		}, 100)
	}

	ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()
	}

	/**
	 * Set up global route handler to automatically manage tabs
	 */
	private setupRouteHandler(): void {
		let previousRoute = ''

		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			debounceTime(100), // Debounce rapid route changes
			takeUntil(this.destroy$)
		).subscribe({
			next: (event: NavigationEnd) => {
				try {
					// console.log('AppComponent: Route navigation detected', event.urlAfterRedirects)
					// Always handle route changes to update query parameters
					this.handleRouteChange(event.urlAfterRedirects)

					previousRoute = event.urlAfterRedirects
				} catch (error) {
					// console.error('AppComponent: Error in route handler', error)
				}
			},
			error: (error) => {
				// console.error('AppComponent: Router event error', error)
			}
		})
	}

	/**
	 * Clean route for comparison by removing query parameters and hash
	 */
	private cleanRouteForComparison(route: string): string {
		let cleanPath = route

		// Remove hash prefix if present
		if (cleanPath.startsWith('#/')) {
			cleanPath = cleanPath.substring(2)
		}

		// Remove query parameters
		cleanPath = cleanPath.split('?')[0]

		// Remove trailing slash for consistency
		if (cleanPath.endsWith('/')) {
			cleanPath = cleanPath.slice(0, -1)
		}

		return cleanPath
	}

	/**
	 * Handle route changes and manage tabs accordingly
	 */
	private handleRouteChange(newRoute: string): void {
		try {
			// console.log('AppComponent: handleRouteChange called with:', newRoute)

			// Handle route change through TabService (RouteMetadataService will handle hash cleaning)
			this.tabService.handleRouteChange(newRoute)

			// Also update active tab's query parameters if needed
			this.tabService.updateActiveTabQueryParams(newRoute)
		} catch (error) {
			// console.error('AppComponent: Error in handleRouteChange', error)
		}
	}

	/**
	 * Initialize default tab if no tabs exist
	 */
	private initializeDefaultTab(): void {
		if (this.tabs().length === 0) {
			// Create default dashboard tab without navigation
			this.tabService.initializeDefaultTab('/lms-plus/panel/dashboard', 'داشبورد ادمین')
		}
	}
}
